import type { ProjectileKind } from '@shefira/shared';
import type Phaser from 'phaser';

import {
  animate,
  dot,
  EARTH,
  EARTH_LIGHT,
  type Feet,
  GOLD_TIP,
  METAL_LIGHT,
  over,
  WHITE,
} from './fx-kit.js';

/**
 * A monster's shot in flight (ADR 0055), drawn from code like the spells'
 * effects: a counter runs from 0 to 1 over the flight and each frame the
 * projectile is redrawn in whole pixels at rounded positions (CLAUDE.md
 * rule 6). Homing, as the server has it: the end of the flight is where the
 * target stands now, asked every frame. Purely visual: the damage arrives
 * on its own as a `hit` when the flight is over.
 */

/** Palette colours (data/assets/palette.gpl) the shots need beyond the kit's. */
const GREEN_DARK = 0x468312;
const GREEN = 0x5da219;
const BLUE_DARK = 0x31679d;
const BLUE = 0x498ace;
const BLUE_LIGHT = 0x66afd7;

/** Shots leave the shooter's chest and land on the target's, not at their feet. */
const CHEST = 11;

export function flyProjectile(
  scene: Phaser.Scene,
  kind: ProjectileKind,
  from: Feet,
  target: () => Feet | undefined,
  ms: number,
): void {
  let last = target() ?? from;
  animate(scene, over({ x: from.x, y: Math.max(from.y, last.y) }), ms, (graphics, progress) => {
    last = target() ?? last;
    graphics.setDepth(over({ x: 0, y: Math.max(from.y, last.y) }));
    const dx = last.x - from.x;
    const dy = last.y - from.y;
    const length = Math.hypot(dx, dy) || 1;
    // Unit vector of the flight, so each shape points where it goes.
    const ux = dx / length;
    const uy = dy / length;
    const x = from.x + dx * progress;
    const y = from.y - CHEST + dy * progress;
    DRAW[kind](graphics, x, y, ux, uy, progress);
  });
}

type Draw = (
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  ux: number,
  uy: number,
  progress: number,
) => void;

/** Dots along the flight, `from` to `to` pixels behind the head, each coloured by its place. */
function along(
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  ux: number,
  uy: number,
  from: number,
  to: number,
  color: (behind: number) => number,
): void {
  for (let behind = from; behind <= to; behind += 1) {
    dot(graphics, x - ux * behind, y - uy * behind, color(behind));
  }
}

const DRAW: Record<ProjectileKind, Draw> = {
  // A steel tip, a wooden shaft, white fletching at the back.
  arrow: (graphics, x, y, ux, uy) => {
    along(graphics, x, y, ux, uy, 0, 6, (behind) =>
      behind === 0 ? METAL_LIGHT : behind >= 5 ? WHITE : behind % 2 === 0 ? EARTH_LIGHT : EARTH,
    );
  },
  // A pale flame that wavers as it drifts, trailing sparks.
  wisp: (graphics, x, y, ux, uy, progress) => {
    const sway = Math.round(Math.sin(progress * Math.PI * 6));
    const cx = x - uy * sway;
    const cy = y + ux * sway;
    for (let oy = -1; oy <= 1; oy += 1) {
      for (let ox = -1; ox <= 1; ox += 1) {
        const corner = ox !== 0 && oy !== 0;
        if (!corner) dot(graphics, cx + ox, cy + oy, GREEN);
      }
    }
    dot(graphics, cx, cy, GOLD_TIP);
    dot(graphics, cx - ux * 3, cy - uy * 3, GREEN);
    dot(graphics, cx - ux * 5 + uy, cy - uy * 5 - ux, GREEN_DARK);
  },
  // A glob of venom, and drops falling behind it.
  venom: (graphics, x, y, ux, uy) => {
    dot(graphics, x, y, GOLD_TIP);
    dot(graphics, x + 1, y, GREEN);
    dot(graphics, x, y + 1, GREEN);
    dot(graphics, x + 1, y + 1, GREEN_DARK);
    dot(graphics, x - ux * 3, y - uy * 3 + 1, GREEN);
    dot(graphics, x - ux * 5, y - uy * 5 + 2, GREEN_DARK);
  },
  // A long shard of ice, bright at the point.
  'ice-shard': (graphics, x, y, ux, uy) => {
    along(graphics, x, y, ux, uy, 0, 4, (behind) =>
      behind === 0 ? WHITE : behind === 1 ? BLUE_LIGHT : behind < 4 ? BLUE : BLUE_DARK,
    );
    dot(graphics, x - ux * 2 - uy, y - uy * 2 + ux, BLUE_LIGHT);
  },
};
